import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCreditCard, FaUniversity, FaLock } from 'react-icons/fa';
import FileUpload from './FileUpload';

type BookingSummary = {
  roomType: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  totalAmount: number;
};

type PaymentFormProps = {
  bookingSummary: BookingSummary;
};

export default function PaymentForm({ bookingSummary }: PaymentFormProps) {
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'bank'>('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    cardNumber: '',
    expiry: '',
    cvv: '',
    nameOnCard: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleFileSelected = (file: File | null) => {
    setUploadedFile(file);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (paymentMethod === 'card') {
      if (!formData.cardNumber.trim()) {
        newErrors.cardNumber = 'Card number is required';
      } else if (!/^\d{16}$/.test(formData.cardNumber.replace(/\s/g, ''))) {
        newErrors.cardNumber = 'Invalid card number';
      }
      
      if (!formData.expiry.trim()) {
        newErrors.expiry = 'Expiry date is required';
      } else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(formData.expiry)) {
        newErrors.expiry = 'Invalid format (MM/YY)';
      }
      
      if (!formData.cvv.trim()) {
        newErrors.cvv = 'CVV is required';
      } else if (!/^\d{3,4}$/.test(formData.cvv)) {
        newErrors.cvv = 'Invalid CVV';
      }
      
      if (!formData.nameOnCard.trim()) {
        newErrors.nameOnCard = 'Name is required';
      }
    } else if (paymentMethod === 'bank') {
      if (!uploadedFile) {
        newErrors.file = 'Please upload your payment receipt';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsProcessing(true);
    
    // Simulate payment processing with CHIP
    try {
      // In a real implementation, this would be a call to the CHIP payment API
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      navigate('/success');
    } catch (error) {
      console.error('Payment processing error:', error);
      setErrors({
        form: 'Payment processing failed. Please try again.'
      });
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Booking Summary</h2>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Room Type:</span>
            <span className="font-medium">{bookingSummary.roomType}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Check-in:</span>
            <span className="font-medium">{bookingSummary.checkIn}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Check-out:</span>
            <span className="font-medium">{bookingSummary.checkOut}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Guests:</span>
            <span className="font-medium">{bookingSummary.guests} {bookingSummary.guests === 1 ? 'Guest' : 'Guests'}</span>
          </div>
          <div className="border-t border-gray-200 my-3"></div>
          <div className="flex justify-between text-lg font-bold">
            <span>Total:</span>
            <span>RM {bookingSummary.totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Payment Method</h2>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            type="button"
            className={`p-4 border rounded-lg flex items-center justify-center transition-colors ${
              paymentMethod === 'card'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => setPaymentMethod('card')}
          >
            <FaCreditCard className="mr-2 text-blue-600" />
            <span>Credit/Debit Card</span>
          </button>
          <button
            type="button"
            className={`p-4 border rounded-lg flex items-center justify-center transition-colors ${
              paymentMethod === 'bank'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => setPaymentMethod('bank')}
          >
            <FaUniversity className="mr-2 text-blue-600" />
            <span>Bank Transfer</span>
          </button>
        </div>

        {paymentMethod === 'card' ? (
          <div className="space-y-4">
            <div>
              <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Card Number
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="cardNumber"
                  name="cardNumber"
                  placeholder="1234 5678 9012 3456"
                  value={formData.cardNumber}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.cardNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isProcessing}
                  maxLength={19}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <FaLock className="text-gray-400" />
                </div>
              </div>
              {errors.cardNumber && <p className="mt-1 text-sm text-red-600">{errors.cardNumber}</p>}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="expiry" className="block text-sm font-medium text-gray-700 mb-1">
                  Expiry Date
                </label>
                <input
                  type="text"
                  id="expiry"
                  name="expiry"
                  placeholder="MM/YY"
                  value={formData.expiry}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.expiry ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isProcessing}
                  maxLength={5}
                />
                {errors.expiry && <p className="mt-1 text-sm text-red-600">{errors.expiry}</p>}
              </div>
              
              <div>
                <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1">
                  CVV
                </label>
                <input
                  type="text"
                  id="cvv"
                  name="cvv"
                  placeholder="123"
                  value={formData.cvv}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.cvv ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isProcessing}
                  maxLength={4}
                />
                {errors.cvv && <p className="mt-1 text-sm text-red-600">{errors.cvv}</p>}
              </div>
            </div>
            
            <div>
              <label htmlFor="nameOnCard" className="block text-sm font-medium text-gray-700 mb-1">
                Name on Card
              </label>
              <input
                type="text"
                id="nameOnCard"
                name="nameOnCard"
                placeholder="John Doe"
                value={formData.nameOnCard}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.nameOnCard ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isProcessing}
              />
              {errors.nameOnCard && <p className="mt-1 text-sm text-red-600">{errors.nameOnCard}</p>}
            </div>
            
            <div className="flex items-center text-sm text-gray-500">
              <FaLock className="mr-2 text-green-600" />
              <span>Your payment information is secure and encrypted</span>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Bank Transfer Instructions</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p>Please transfer the payment to the following account:</p>
              <div className="bg-white p-3 rounded-md">
                <p><span className="font-medium">Bank:</span> Maybank</p>
                <p><span className="font-medium">Account Name:</span> MIKHAEL HOMESTAY</p>
                <p><span className="font-medium">Account Number:</span> 1234 5678 9012</p>
              </div>
              <p>After making the transfer, please upload the payment receipt below:</p>
              <div className="mt-2">
                <FileUpload onFileSelected={handleFileSelected} />
                {errors.file && <p className="mt-1 text-sm text-red-600">{errors.file}</p>}
              </div>
            </div>
          </div>
        )}
      </div>

      {errors.form && (
        <div className="bg-red-50 p-4 rounded-lg text-red-800">
          {errors.form}
        </div>
      )}

      <div>
        <button
          type="submit"
          disabled={isProcessing}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-white border-solid rounded-full animate-spin mr-2"></div>
              Processing Payment...
            </span>
          ) : (
            <span>Complete Payment</span>
          )}
        </button>
      </div>
    </form>
  );
}
